import {
  getCurrencyByLocale,
  renderPrice,
  getCurrencySymbolByLocale,
} from "@/apiUtils/commonUtils";
import { getLangFromLocale } from "./common";
import { FilteredFacet } from "@/apiUtils/algoliaUtils/types";
import { algoliaQueryParams } from "@/apiUtils/algoliaUtils/constants";

// "p" is a synthesized display key (pmin+pmax merged into one readable chip) —
// it never comes from the URL or from Sanity, so it can't be sourced from `filters`.
const PRICE_DISPLAY_KEY = "p";

/**
 * Updates the search parameters by adding, removing, or replacing a value for a specific key.
 * Removes the key if the value is an empty string. Directly replaces the value if the key is "pmin" or "pmax".
 */
export const getUpdatedSearchParams = (
  currentParams: Record<string, string> | undefined,
  key: string,
  value: string,
): Record<string, string> => {
  if (!currentParams) return value === "" ? {} : { [key]: value };

  if (key === "pmin" || key === "pmax" || key === "sort") {
    if (value === "") {
      const newParams = { ...currentParams };
      delete newParams[key];
      return newParams;
    }
    return { ...currentParams, [key]: value };
  }

  if (value === "") {
    const newParams = { ...currentParams };
    delete newParams[key];
    return newParams;
  }

  const existingValues = currentParams[key]?.split("|") || [];
  const valueExists = existingValues.includes(value);

  let updatedValues: string[];
  if (valueExists) {
    updatedValues = existingValues.filter((v) => v !== value);
  } else {
    updatedValues = [...existingValues, value];
  }

  const newParams = { ...currentParams };
  if (updatedValues.length > 0) {
    newParams[key] = updatedValues.join("|");
  } else {
    delete newParams[key];
  }

  delete newParams.page;
  return newParams;
};

export const removerSearchValue = (
  currentParams: Record<string, string> | undefined,
  key: string,
  value: string,
): Record<string, string> => {
  if (!currentParams) return {};
  const newParams = { ...currentParams };
  if (key === "p") {
    delete newParams.pmin;
    delete newParams.pmax;
  }

  const existingValues = currentParams[key]?.split("|") || [];
  const updatedValues = existingValues.filter((v) => v !== value);

  if (updatedValues.length > 0) {
    newParams[key] = updatedValues.join("|");
  } else {
    delete newParams[key];
  }
  delete newParams.page;
  return newParams;
};

const filterFormatedPrice = (
  lang: string,
  currency: string,
  price: number | string,
  locale?: string,
) => {
  const symbol = locale ? getCurrencySymbolByLocale(locale) : currency;
  const newPrice = renderPrice(lang, symbol, price);
  const temp = newPrice.split(" ");
  return lang === "ar"
    ? `${temp[1]} ${temp[0]?.split(".")[0]}`
    : `${temp[0]} ${temp[1]?.split(".")[0]}`;
};

/**
 * Resolves URL key for a filter — uses Sanity filterParams only.
 * No static filterTypeMap fallback.
 */
export const getFilterParamKey = (filter: { filterParams?: string }): string =>
  filter?.filterParams ?? "_";

export const findFilterByKey = (key: string, filters: FilteredFacet[]): FilteredFacet | undefined =>
  filters?.find((item) => item?.filterParams === key);

export const getFacetTypeByKey = (key: string, filters?: FilteredFacet[]): string | undefined =>
  filters?.find((f) => f?.filterParams === key)?.type;

// Category isn't part of `filters[].filterParams` (it's applied outside the filter
// system), but it's a real, reserved facet key — see the CMS's own
// STATIC_FILTER_MAPPINGS.cat in magrabi-content-management/src/utils/urlKeyUtils.ts.
const CATEGORY_KEY = algoliaQueryParams.CATEGORY;
const PRICE_MIN_KEY = algoliaQueryParams.PRICE_MIN;
const PRICE_MAX_KEY = algoliaQueryParams.PRICE_MAX;

/**
 * True when a query param key represents an actual applied filter, as opposed to
 * incidental params (UTM tracking, etc.) that ended up in the URL but map to no facet.
 */
const isKnownFilterKey = (key: string, filters?: FilteredFacet[]): boolean => {
  if (key === CATEGORY_KEY || key === PRICE_DISPLAY_KEY) return true;

  // pmin/pmax have no filterParams of their own in Sanity — the price filterItem
  // carries no code, so recognize them by the price filter's presence in config.
  if (key === PRICE_MIN_KEY || key === PRICE_MAX_KEY) {
    return !!filters?.some((f) => f?.filterItemType === "price");
  }

  return !!filters?.some(
    (f) => f?.filterParams === key || f?.booleanMappings?.some((m) => m?.filterParams === key),
  );
};

export const extractSelectedSearchParams = (
  currentSearchParams: Record<string, string>,
  locale: string,
  filters?: FilteredFacet[],
): Record<string, string> => {
  const lang = getLangFromLocale(locale);
  const currency = getCurrencyByLocale(locale);
  const draftParams: Record<string, string> = {};
  Object.entries(currentSearchParams || {}).forEach(([key, value]) => {
    if (isKnownFilterKey(key, filters)) draftParams[key] = value;
  });
  if (draftParams.hasOwnProperty("pmin") && draftParams.hasOwnProperty("pmax")) {
    const pMin = filterFormatedPrice(lang, currency, draftParams.pmin, locale);
    const pMax = filterFormatedPrice(lang, currency, draftParams.pmax, locale);
    const p = lang === "ar" ? `${pMax} - ${pMin}` : `${pMin} - ${pMax}`;
    draftParams.p = p;
    delete draftParams.pmin;
    delete draftParams.pmax;
  }
  delete draftParams.page;
  if (draftParams.hasOwnProperty("sort")) delete draftParams.sort;
  return draftParams;
};
